import {inject, InjectionToken, Provider} from '@angular/core'
import {BettingRulesDto} from '@/api/player-api/models/betting-rules-dto'
import {SiteConfigService} from '@/services/site-config.service'
import {BetslipCreateService} from '@/features/betslip/betslip-create/betslip-create.service'
import {BetslipUiDataUpdateService} from '@/features/betslip/betslip-create/lib/betslip-ui-data-update.service'
import {BetslipSolverFactory} from '@/features/betslip/betslip-create/lib/solvers/betslip-solver-factory'
import {BetslipValidationService} from '@/features/betslip/betslip-create/lib/validators/betslip-validation.service'
import {
	BetslipCombinationValidator
} from '@/features/betslip/betslip-create/lib/validators/implementations/betslip-combination-validator'
import {
	BetslipSelectionValidator
} from '@/features/betslip/betslip-create/lib/validators/implementations/betslip-selection-validator'
import {BetslipStakeValidator} from '@/features/betslip/betslip-create/lib/validators/implementations/betslip-stake-validator'
import {BetslipSystemValidator} from '@/features/betslip/betslip-create/lib/validators/implementations/betslip-system-validator'
import {BetslipPayoutValidator} from '@/features/betslip/betslip-create/lib/validators/implementations/betslip-payout-validator'
import {BetslipOddsValidator} from '@/features/betslip/betslip-create/lib/validators/implementations/betslip-odds-validator'
import {BetslipLoadStorageService} from '@/features/betslip/betslip-create/storage/betslip-load-storage.service'

/** Where an isolated betslip reads its Betting Limits from, in place of the site config. */
export const ISOLATED_BETTING_RULES = new InjectionToken<() => BettingRulesDto>('ISOLATED_BETTING_RULES')

/**
 * A second, self-contained BetslipCreateService with everything it calculates with, reading its
 * Betting Limits from `bettingRules` instead of the site config.
 *
 * Everything in betslip-create is `providedIn: 'root'` and reads `SiteConfigService.bettingRules`,
 * so each class that does is provided again here. Put these on a component (or TestBed) and that
 * injector gets a betslip of its own: it does not load the player's saved betslip, and the root
 * save/risk services keep listening to the root betslip, so the player's betslip and its storage
 * are never touched.
 *
 * Used by the dev-tools Betslip workbench and by `capping-vectors.spec.ts`, so the
 * workbench and the replayed vectors run on exactly the same wiring.
 *
 * Pass `bettingRules` directly, or leave it out and provide `ISOLATED_BETTING_RULES` next to these
 * providers when the rules come from something injected alongside them.
 */
export function provideIsolatedBetslip(bettingRules?: () => BettingRulesDto): Provider[] {
	return [
		...(bettingRules ? [{provide: ISOLATED_BETTING_RULES, useValue: bettingRules}] : []),
		{
			provide: SiteConfigService,
			useFactory: (): Pick<SiteConfigService, 'bettingRules'> => {
				const rules = inject(ISOLATED_BETTING_RULES)
				return {
					get bettingRules() {
						return rules()
					}
				}
			}
		},
		BetslipCreateService,
		BetslipUiDataUpdateService,
		BetslipSolverFactory,
		BetslipValidationService,
		BetslipCombinationValidator,
		BetslipSelectionValidator,
		BetslipStakeValidator,
		BetslipSystemValidator,
		BetslipPayoutValidator,
		BetslipOddsValidator,
		{provide: BetslipLoadStorageService, useValue: {loadFromLocalStorage: () => undefined}}
	]
}
